/**
 * Neo client-side bindings (Phase 4).
 *
 * Thin wrappers around the POE proxy routes at /api/neo/*. Every response
 * carries { source: 'live' | 'poe' | 'stub' | 'offline' } so UI can
 * distinguish live Neo, POE-derived fallback signals, local dev stubs, and
 * production offline fallback.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';
import { parseNeoStreamFrame, type NeoStreamEvent } from './neoStream.js';

export type { NeoStreamEvent } from './neoStream.js';

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Types - mirror server/lib/neo/client.ts exactly
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface ActionFeedItem {
  id: string;
  kind: 'hot_lead' | 'stale_deal' | 'follow_up_due' | 'dd_complete' | 'data_gap' | 'proposal_ready' | 'chain_paused' | 'relationship_cooling';
  title: string;
  reason: string;
  priority: number;
  dealId?: string;
  partyId?: string;
  confidence?: 'high' | 'medium' | 'low';
  createdAt: string;
}

export interface ActionProposal {
  id: string;
  type: ActionFeedItem['kind'];
  title: string;
  summary: string;
  recommendation: string;
  confidence: 'high' | 'medium' | 'low';
  sourceSkillId: string;
  autonomyLevel: 1 | 2 | 3 | 4 | 5;
  status: 'pending' | 'approved' | 'rejected' | 'dismissed' | 'expired';
  dealId?: string;
  partyId?: string;
  data?: Record<string, unknown>;
  createdAt: string;
}

export interface MemoryFact {
  id: string;
  contactId: string;
  factType: 'preference' | 'history' | 'objection' | 'milestone' | 'note';
  content: string;
  confidence: number;
  source: string;
  createdAt: string;
}

export interface LearningInsight {
  id: string;
  scope: 'deal' | 'contact' | 'global';
  scopeId: string;
  title: string;
  description: string;
  metric?: string;
  evidenceCount: number;
  createdAt: string;
}

export interface InterruptedChain {
  runId: string;
  skillId: string;
  question: string;
  context: Record<string, unknown>;
  pausedAt: string;
}

export interface PersonaVector {
  userId: string;
  experienceLevel: 'newbie' | 'intermediate' | 'expert';
  role: 'acquisitions' | 'operations' | 'admin' | 'closer';
  communicationStyle: 'concise' | 'detailed' | 'casual' | 'formal';
  toneMatchScore: number;
  updatedAt: string;
}

export type NeoSource = 'live' | 'poe' | 'stub' | 'offline';

export interface NeoStatus {
  source: NeoSource;
  live: boolean;
  local?: boolean;
  externalSource?: NeoSource;
}

export interface SkillRunResult {
  ok: boolean;
  skillId: string;
  status: 'completed' | 'proposed' | 'failed';
  proposalId?: string;
  output?: Record<string, unknown>;
  error?: string;
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// React Query hooks
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function useNeoStatus() {
  return useQuery({
    queryKey: ['neo', 'status'],
    queryFn: () => api.get<NeoStatus>('/api/neo/status'),
    staleTime: 60_000,
  });
}

export function useActionFeed(limit = 20) {
  return useQuery({
    queryKey: ['neo', 'action-feed', limit],
    queryFn: () => api.get<{ items: ActionFeedItem[]; source: NeoSource }>(`/api/neo/action-feed?limit=${limit}`),
    staleTime: 15_000,
  });
}

export function useProposals(opts: { dealId?: string; status?: ActionProposal['status']; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (opts.dealId) qs.set('dealId', opts.dealId);
  if (opts.status) qs.set('status', opts.status);
  if (opts.limit) qs.set('limit', String(opts.limit));
  return useQuery({
    queryKey: ['neo', 'proposals', opts],
    queryFn: () => api.get<{ items: ActionProposal[]; source: NeoSource }>(`/api/neo/proposals?${qs}`),
    staleTime: 15_000,
  });
}

export function useUpdateProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' | 'dismiss' }) =>
      api.post<{ ok: boolean; source: NeoSource }>(`/api/neo/proposals/${id}/${action}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['neo', 'proposals'] });
      qc.invalidateQueries({ queryKey: ['neo', 'action-feed'] });
    },
  });
}

// Neo human-feedback loop (POE-side; server/routes/neo-feedback.ts). Captures
// the user's signals on action-feed items + proposals and re-ranks the feed by
// the accumulated per-kind weights, so the feed improves as feedback accrues.
export type NeoFeedbackSignal =
  | 'useful'
  | 'not_useful'
  | 'wrong_intent'
  | 'wrong_priority'
  | 'dismissed'
  | 'accepted';

export interface NeoFeedbackInput {
  targetType: 'action_feed' | 'proposal';
  targetId: string;
  kind?: string;
  signal: NeoFeedbackSignal;
  correctedKind?: string;
  correctedPriority?: number;
  dealId?: string;
  partyId?: string;
  note?: string;
}

export function useNeoFeedbackWeights() {
  return useQuery({
    queryKey: ['neo', 'feedback-weights'],
    queryFn: () => api.get<{ weights: Record<string, number> }>('/api/neo-feedback/weights'),
    staleTime: 60_000,
  });
}

export function useSubmitNeoFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NeoFeedbackInput) =>
      api.post<{ ok: boolean; id?: string }>('/api/neo-feedback', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['neo', 'feedback-weights'] });
      qc.invalidateQueries({ queryKey: ['neo', 'action-feed'] });
    },
  });
}

/** Re-rank an action feed by per-kind feedback weights (POE-side learning). */
export function rankActionFeed(
  items: ActionFeedItem[],
  weights?: Record<string, number>,
): ActionFeedItem[] {
  if (!weights) return items;
  return [...items].sort((a, b) => {
    const aw = (a.priority ?? 0) * (weights[a.kind] ?? 1);
    const bw = (b.priority ?? 0) * (weights[b.kind] ?? 1);
    return bw - aw;
  });
}

/** Action feed re-ranked by the user's accumulated feedback weights. */
export function useRankedActionFeed(limit = 20) {
  const feed = useActionFeed(limit);
  const weights = useNeoFeedbackWeights();
  const rankedItems = rankActionFeed(feed.data?.items ?? [], weights.data?.weights);
  return { ...feed, rankedItems, weights: weights.data?.weights };
}

export function useMemoryRecall(contactId: string | null | undefined) {
  return useQuery({
    queryKey: ['neo', 'memory-recall', contactId],
    queryFn: () => api.get<{ facts: MemoryFact[]; source: NeoSource }>(`/api/neo/memory/recall?contact=${contactId}`),
    enabled: !!contactId,
    staleTime: 60_000,
  });
}

export function useLearningInsights(opts: { dealId?: string; contactId?: string }) {
  const qs = new URLSearchParams();
  if (opts.dealId) qs.set('dealId', opts.dealId);
  if (opts.contactId) qs.set('contactId', opts.contactId);
  return useQuery({
    queryKey: ['neo', 'learning-insights', opts],
    queryFn: () => api.get<{ insights: LearningInsight[]; source: NeoSource }>(`/api/neo/learning/insights?${qs}`),
    enabled: !!(opts.dealId || opts.contactId),
    staleTime: 60_000,
  });
}

export function useInterruptedChains() {
  return useQuery({
    queryKey: ['neo', 'interrupted-chains'],
    queryFn: () => api.get<{ chains: InterruptedChain[]; source: NeoSource }>('/api/neo/chains/interrupted'),
    staleTime: 15_000,
  });
}

export function useResumeChain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, action, payload }: { runId: string; action: string; payload?: Record<string, unknown> }) =>
      api.post<{ ok: boolean; source: NeoSource }>(`/api/neo/chains/${runId}/resume`, { action, payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['neo', 'interrupted-chains'] }),
  });
}

export function usePersona(userId: string | null | undefined) {
  return useQuery({
    queryKey: ['neo', 'persona', userId],
    queryFn: () => api.get<{ persona: PersonaVector | null; source: NeoSource }>(`/api/neo/persona/${userId}`),
    enabled: !!userId,
    staleTime: 5 * 60_000,
  });
}

export function useRunSkill() {
  return useMutation({
    mutationFn: ({ skillId, input }: { skillId: string; input?: Record<string, unknown> }) =>
      api.post<{ result: SkillRunResult; source: NeoSource }>('/api/neo/run-skill', { skillId, input }),
  });
}

export function useDispatchNL() {
  return useMutation({
    mutationFn: (text: string) =>
      api.post<{ result: SkillRunResult; source: NeoSource }>('/api/neo/dispatch-natural-language', { text }),
  });
}

// Sync chat (one-shot)
export function useChat() {
  return useMutation({
    mutationFn: ({ prompt, context }: { prompt: string; context?: Record<string, unknown> }) =>
      api.post<{ reply: string; source: NeoSource }>('/api/neo/chat', { prompt, context }),
  });
}

/**
 * Stream chat replies from Neo. Returns an async generator yielding structured
 * events so the panel can show status, tool calls, text, errors, and done.
 * Falls back to one full chunk if SSE isn't available.
 */
export async function* streamChat(
  prompt: string,
  context: Record<string, unknown> = {},
  signal?: AbortSignal,
): AsyncGenerator<NeoStreamEvent, void, void> {
  const res = await fetch('/api/neo/chat-stream', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ prompt, context }),
    credentials: 'include',
    signal,
  });
  if (!res.ok || !res.body) {
    yield { type: 'error', message: 'Failed to reach Neo.' };
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      const event = parseNeoStreamFrame(frame);
      if (!event) continue;
      if (event.type === 'done') {
        yield event;
        return;
      }
      yield event;
    }
  }
}
